import { fetchUtils, DataProvider } from 'react-admin';
import queryString from 'query-string';

// Use relative URL in production (same origin), configurable for development
const API_URL = import.meta.env.VITE_API_URL || '';

const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
    const headers = new Headers(options.headers);
    headers.set('Accept', 'application/json');

    // JWT is in an httpOnly cookie — sent automatically by the browser for same-origin requests.
    // No Authorization header needed.
    return fetchUtils.fetchJson(url, { ...options, headers });
};

/**
 * Some admin endpoints return a plain JSON array rather than a paged
 * { items, totalCount } envelope — GET /api/admin/node-claims is one — and they ignore the
 * pageNumber/pageSize/sortBy/sortDirection query parameters sent below.
 *
 * Without this, every list control over such an endpoint is silently inert: clicking a column
 * header re-sorts nothing, filters filter nothing, and the pagination bar lies about what it
 * is showing. Rather than leave dead controls on screen, do the work here when the server
 * did not: if the response was a bare array, the server has not filtered, sorted or paged it,
 * so the client must. Fleet sizes for these endpoints are tens of rows, not thousands.
 */
const applyClientSideListParams = <T extends Record<string, unknown>>(
    rows: T[],
    params: { filter?: Record<string, unknown>; sort?: { field: string; order: string }; pagination?: { page: number; perPage: number } },
): { data: T[]; total: number } => {
    let result = rows;

    const filter = params.filter ?? {};
    // Only keys the rows actually carry. A bare array is not always unfiltered: GET
    // /api/admin/devices applies its own `filter` and `q` query parameters and answers a bare
    // array, and no row has a `filter` or `q` field - so matching them here dropped every row
    // and Provisioned Devices read 0 for every choice (#1679). A key no row carries is a
    // server parameter, already applied by the server.
    const activeFilters = Object.entries(filter).filter(
        ([field, value]) =>
            value !== undefined && value !== null && value !== ''
            && rows.some(row => Object.prototype.hasOwnProperty.call(row, field))
    );
    if (activeFilters.length > 0) {
        result = result.filter(row =>
            activeFilters.every(([field, value]) =>
                String(row[field] ?? '').toLowerCase().includes(String(value).toLowerCase())
            )
        );
    }

    const total = result.length;

    if (params.sort?.field) {
        const { field, order } = params.sort;
        const direction = order.toLowerCase() === 'desc' ? -1 : 1;
        result = [...result].sort((a, b) => {
            const left = a[field];
            const right = b[field];
            if (left === right) return 0;
            // Nulls last regardless of direction: an unclaimed row sorted by claimedAt should
            // not push the rows the operator is looking for off the top of the page.
            if (left === null || left === undefined) return 1;
            if (right === null || right === undefined) return -1;
            return (left < right ? -1 : 1) * direction;
        });
    }

    if (params.pagination) {
        const { page, perPage } = params.pagination;
        const start = (page - 1) * perPage;
        result = result.slice(start, start + perPage);
    }

    return { data: result, total };
};

export const dataProvider: DataProvider = {
    getList: async (resource, params) => {
        const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
        const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };

        const query = {
            pageNumber: page,
            pageSize: perPage,
            sortBy: field,
            sortDirection: order.toLowerCase(),
            ...params.filter,
        };

        const url = `${API_URL}/api/admin/${resource}?${queryString.stringify(query)}`;
        const { json } = await httpClient(url);

        // A bare array means the server paged/sorted/filtered nothing — do it here.
        if (Array.isArray(json)) {
            return applyClientSideListParams(json, params);
        }

        return {
            data: json.items || json,
            total: json.totalCount || json.length,
        };
    },

    getOne: async (resource, params) => {
        const url = `${API_URL}/api/admin/${resource}/${params.id}`;
        const { json } = await httpClient(url);
        return { data: json };
    },

    getMany: async (resource, params) => {
        const query = { ids: params.ids.join(',') };
        const url = `${API_URL}/api/admin/${resource}?${queryString.stringify(query)}`;
        const { json } = await httpClient(url);
        return { data: json.items || json };
    },

    getManyReference: async (resource, params) => {
        const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
        const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };

        const query = {
            [params.target]: params.id,
            pageNumber: page,
            pageSize: perPage,
            sortBy: field,
            sortDirection: order.toLowerCase(),
            ...params.filter,
        };

        const url = `${API_URL}/api/admin/${resource}?${queryString.stringify(query)}`;
        const { json } = await httpClient(url);

        return {
            data: json.items || json,
            total: json.totalCount || json.length,
        };
    },

    create: async (resource, params) => {
        const url = `${API_URL}/api/admin/${resource}`;
        const { json } = await httpClient(url, {
            method: 'POST',
            body: JSON.stringify(params.data),
        });
        return { data: json };
    },

    update: async (resource, params) => {
        const url = `${API_URL}/api/admin/${resource}/${params.id}`;
        const { json } = await httpClient(url, {
            method: 'PUT',
            body: JSON.stringify(params.data),
        });
        return { data: json };
    },

    updateMany: async (resource, params) => {
        await Promise.all(
            params.ids.map(id =>
                httpClient(`${API_URL}/api/admin/${resource}/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(params.data),
                })
            )
        );
        return { data: params.ids };
    },

    delete: async (resource, params) => {
        const url = `${API_URL}/api/admin/${resource}/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });
        return { data: json || params.previousData };
    },

    deleteMany: async (resource, params) => {
        await Promise.all(
            params.ids.map(id =>
                httpClient(`${API_URL}/api/admin/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        );
        return { data: params.ids };
    },
};
